import { Component, OnDestroy } from '@angular/core'
import { Store } from '@ngrx/store'
import {
    selectCaseHasSavedHierarchicalSelections,
    selectHierarchicalSelectionRootName,
    selectSavedAutoCellTypeDetectionRootNames,
    selectSavedHierarchicalSelectionRootNames,
    selectSelectionState,
} from '../../../state/selection/selection.selectors'
import { selectionActions } from '../../../state/selection/selection.actions'
import { Subject, takeUntil, take } from 'rxjs'
import {
    selectCaseSegObjectPropertyMap,
    selectIsSharedCase,
} from '../../../state/case/case.selectors'
import { GenericConfirmationDialogModel } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { Dialog } from '@angular/cdk/dialog'
import { GenericConfirmationDialogComponent } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { SaveSelectionAsDialogComponent } from '../../save-selection-as-dialog/save-selection-as-dialog.component'
import { DownloadService } from '../../../shared/services/download.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { SelectionType } from 'src/app/state/selection/selection.state'

@Component({
    selector: 'app-saved-hierarchical-selection-roots',
    templateUrl: './saved-hierarchical-selection-roots.component.html',
    styleUrls: ['./saved-hierarchical-selection-roots.component.scss'],
})
export class SavedHierarchicalSelectionRootsComponent implements OnDestroy {
    readonly hasSavedHierarchicalSelections$ = this.store.select(
        selectCaseHasSavedHierarchicalSelections
    )
    readonly currentHierarchicalSelectionRootName$ = this.store.select(
        selectHierarchicalSelectionRootName
    )
    savedHierarchicalSelectionNames$ = this.store.select(selectSavedHierarchicalSelectionRootNames)
    readonly isSharedCase$ = this.store.select(selectIsSharedCase)
    readonly selectionState$ = this.store.select(selectSelectionState)
    isUnsupervisedSelection = false

    private readonly destroy$ = new Subject<void>()

    constructor(
        private readonly store: Store,
        private readonly dialog: Dialog,
        private readonly toaster: ToasterService,
        private readonly downloader: DownloadService
    ) {
        this.selectionState$.pipe().subscribe(selectionState => {
            if (selectionState.type === SelectionType.Hierarchical) {
                this.isUnsupervisedSelection = false
                this.savedHierarchicalSelectionNames$ = this.store.select(
                    selectSavedHierarchicalSelectionRootNames
                )
            } else {
                this.isUnsupervisedSelection = true
                this.savedHierarchicalSelectionNames$ = this.store.select(
                    selectSavedAutoCellTypeDetectionRootNames
                )
            }
        })
    }

    readonly currentSegObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    createNewHierarchicalSelectionRoot(): void {
        const dialogRef = this.dialog.open<string>(SaveSelectionAsDialogComponent, {
            autoFocus: 'first-tabbable',
        })

        // Subscribe to the observable to get the current value of `segObjectPropertyMap`
        this.currentSegObjectPropertyMap$.subscribe(segObjectPropertyMap => {
            // Ensure that you handle segObjectPropertyMap properly if it's emitted by the observable
            const segSource = segObjectPropertyMap?.segSource
            dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(name => {
                // If no name is provided, exit early
                if (!name) return
                // Dispatch actions using the name and the segSource from segObjectPropertyMap
                this.store.dispatch(
                    selectionActions.saveNewHierarchicalSelectionRoot({ name, segSource })
                )
            })
        })
    }

    setSelection(name: string): void {
        this.store.dispatch(selectionActions.setHierarchicalSelectionRoot({ name }))
    }

    downloadHierarchicalSelections(): void {
        this.currentHierarchicalSelectionRootName$.pipe(take(1)).subscribe(rootName => {
            if (!rootName) return
            if (this.isUnsupervisedSelection) {
                this.downloader.downloadUnsupervisedSelections(rootName).subscribe({
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Failed to download selections.')
                    },
                })
            } else {
                this.downloader.downloadHierarchicalSelections(rootName).subscribe({
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Failed to download selections.')
                    },
                })
            }
        })
    }

    deleteCurrentHierarchicalSelectionRoot(selectionName: string | undefined): void {
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this hierarchical selection?',
            item: selectionName,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(selectionActions.deleteHierarchicalSelectionRoot())
        })
    }
}
